import { TenantId, SiteId, AssetId, AssetSource, CameraId } from "@visioni/a3s-schema";
import { z } from "zod/v4";

/**
 * JSON-compatible Zod schemas.
 *
 * (Note `undefined` is not a valid JSON value but indicates to the serializer to exclude the key.)
 */
export const JSONValue: z.ZodType<JSONValue> = z.lazy(() =>
  z.union([z.string(), z.number(), z.boolean(), z.null(), z.undefined(), JSONObject, JSONArray]),
);
export const JSONObject: z.ZodType<JSONObject> = z.lazy(() => z.record(z.string(), JSONValue));
export const JSONArray: z.ZodType<JSONArray> = z.lazy(() => z.array(JSONValue));

/**
 * JSON-compatible type definitions
 *
 * (Note `undefined` is not a valid JSON value but indicates to the serializer to exclude the key.)
 */
export type JSONValue = string | number | boolean | null | undefined | JSONObject | JSONArray;
export type JSONObject = { [key: string]: JSONValue };
export type JSONArray = JSONValue[];

export const TenantIdSchema = z.codec(z.string(), z.custom<TenantId>(), {
  decode: (encoded) => TenantId.fromString(encoded),
  encode: (decoded) => TenantId.toString(decoded),
});

export const SiteIdSchema = z.codec(z.string(), z.custom<SiteId>(), {
  decode: (encoded) => SiteId.fromString(encoded),
  encode: (decoded) => SiteId.toString(decoded),
});

export const AssetIdSchema = z.codec(z.string(), z.custom<AssetId>(), {
  decode: (encoded) => AssetId.fromString(encoded),
  encode: (decoded) => AssetId.toString(decoded),
});

/**
 * Creates a Zod schema for AssetId restricted to a specific source.
 *
 * @param source - The expected source
 * @returns A Zod codec that validates and produces AssetId<S>
 */
export const AssetIdSchemaOf = <S extends AssetSource>(source: S) =>
  z.codec(z.string(), z.custom<AssetId<S>>(), {
    decode: (encoded, ctx) => {
      const id = AssetId.fromString(encoded);
      if (!AssetId.isSource(id, source)) {
        ctx.issues.push({ code: "custom", input: encoded, message: `Expected ${source} asset` });
        return z.NEVER;
      }
      return id;
    },
    encode: (decoded) => AssetId.toString(decoded),
  });

export const CameraIdSchema = z.codec(z.string(), z.custom<CameraId>(), {
  decode: (encoded) => CameraId.fromString(encoded),
  encode: (decoded) => CameraId.toString(decoded),
});

export const InstalledCameraSubject = z.codec(
  z.string(),
  z.object({ tenant: TenantIdSchema, site: SiteIdSchema, asset: AssetIdSchema, camera: CameraIdSchema }),
  {
    decode: (encoded, ctx) => {
      const parts = encoded.split("/");
      if (parts.length !== 4) {
        ctx.issues.push({
          code: "invalid_format",
          format: "includes",
          input: encoded,
          message: "Subject is not in the expected format: {tenant}/{site}/{asset}/{camera}",
        });
        return z.NEVER;
      }
      const [tenant, site, asset, camera] = parts as [string, string, string, string];
      return { tenant, site, asset, camera };
    },
    encode: (decoded) => `${decoded.tenant}/${decoded.site}/${decoded.asset}/${decoded.camera}`,
  },
);

export type InstalledCameraSubject = z.infer<typeof InstalledCameraSubject>;

/** Subject schema for installed-asset-level messages (tenant/site/asset, no camera). */
export const InstalledAssetSubject = z.codec(
  z.string(),
  z.object({ tenant: TenantIdSchema, site: SiteIdSchema, asset: AssetIdSchema }),
  {
    decode: (encoded, ctx) => {
      const parts = encoded.split("/");
      if (parts.length !== 3) {
        ctx.issues.push({
          code: "invalid_format",
          format: "includes",
          input: encoded,
          message: "Subject is not in the expected format: {tenant}/{site}/{asset}",
        });
        return z.NEVER;
      }
      const [tenant, site, asset] = parts as [string, string, string];
      return { tenant, site, asset };
    },
    encode: (decoded) => `${decoded.tenant}/${decoded.site}/${decoded.asset}`,
  },
);

export type InstalledAssetSubject = z.infer<typeof InstalledAssetSubject>;

/**
 * Subject schema for asset-level messages (without tenant/site/camera context).
 * Restricted to hub assets only.
 */
export const AssetSubject = z.codec(z.string(), z.object({ asset: AssetIdSchemaOf("fleet") }), {
  decode: (encoded) => ({ asset: encoded }),
  encode: (decoded) => decoded.asset,
});

export type AssetSubject = z.infer<typeof AssetSubject>;

const Subject = InstalledCameraSubject;

const DateTime = z.codec(z.iso.datetime(), z.date(), {
  decode: (encoded) => new Date(encoded),
  encode: (decoded) => decoded.toISOString(),
});

export type MessageType<TType extends string> = `${TType}Command` | `${TType}Event` | `${TType}State`;

export type MessageOptions<
  TType extends MessageType<string>,
  TData extends z.ZodType,
  TSub extends z.ZodType = typeof Subject,
> = {
  /** The message type identifier. Must end in 'Command', 'Event', or 'State'. */
  typ: TType;
  /** The message schema version */
  ver: string;
  /** The schema for the message payload data */
  dat: TData;
  /** Optional custom subject schema. Defaults to standard Subject. */
  sub?: TSub;
};

/**
 * Creates a message codec that transforms between decoded (TypeScript) and encoded (JSON) forms.
 *
 * Decoded form: { typ: literal, ver: literal, pub: Date, sub: Subject, dat: TData }
 * Encoded form: { typ: string, ver: string, pub: string, sub: string, dat: JSONObject }
 *
 * @param opt - The options for the codec.
 * @returns A Zod codec for bidirectional transformation
 */
export const Message = <
  TType extends MessageType<string>,
  TData extends z.ZodType,
  TSub extends z.ZodType = typeof Subject,
>(
  opt: MessageOptions<TType, TData, TSub>,
) =>
  z.object({
    typ: z.literal(opt.typ),
    ver: z.literal(opt.ver),
    pub: DateTime,
    sub: opt.sub ?? Subject,
    dat: opt.dat,
  });

export { DateTime };

/**
 * Unknown message for initial parsing when the specific message type is not yet known.
 * Use this to parse the envelope and then dispatch to specific handlers based on typ/ver.
 */
export const UnknownMessage = z.object({
  typ: z.string(),
  ver: z.string(),
  pub: DateTime,
  sub: z.string(),
  dat: JSONObject,
});
export type UnknownMessage = z.infer<typeof UnknownMessage>;
